import { FC, useEffect } from 'react';

import { useEntitySchema, useStack9, Utils } from '@april9au/stack9-react';
import * as ui from '@april9au/stack9-ui';
import { Col, Divider, Row } from 'antd';
import dayjs from 'dayjs';
import { z } from 'zod';

import { DBResourceWithRelated } from '../../models/Resource';
import { DBOrganisation } from '../../models/stack9/Organisation';
import {
  DBResource,
  ResourceTypeOptionType,
  ResourceWithRelated,
} from '../../models/stack9/Resource';
import { DBTopic } from '../../models/stack9/Topic';

const S9EntityActions = ui.S9EntityActions.default;
const S9Drawer = ui.S9Drawer.default;
const S9Form = ui.S9Form.default;
const S9Fieldset = ui.S9Fieldset.default;
const S9Button = ui.S9Button.default;
const S9PageTitle = ui.S9PageTitle.default;
const S9DateField = ui.S9DateField.default;
const { dateComparisonRule } = ui.S9DatePicker;

const { useForm } = ui.S9Form;

interface Props {
  open: boolean;
  id: number | undefined;
  canEdit?: boolean;
  onSaved(): void;
  onDeleted(): void;
  onClose(): void;
}

const ResourceDrawer: FC<Props> = ({
  open,
  id = 0,
  canEdit = true,
  onSaved,
  onDeleted,
  onClose,
}) => {
  const [form] = useForm();
  const { useWatch } = ui.S9Form;
  const { config } = useStack9();

  const { schema, isValidating } = useEntitySchema('resource');

  const { handleSave, isLoading: isSaving } = ui.useEntityActions('resource');

  const { data: record } = ui.useScreenQueryById<DBResourceWithRelated>(
    'getResourceById',
    id,
    { shouldFetch: !!id },
  );

  const resourceType = useWatch<ResourceTypeOptionType>(
    ['resource_type'],
    form,
  );

  useEffect(() => {
    form.resetFields();
  }, [open, form, id, record]);

  return (
    <S9Drawer
      open={open}
      closable
      keyboard={false}
      maskClosable={false}
      onClose={onClose}
      width={800}
    >
      <S9Form
        form={form}
        disabled={isSaving || isValidating || canEdit === false}
        initialValues={record}
        loading={isValidating}
        onFinish={async values => {
          const formValues = ResourceWithRelated.omit({
            file_to_be_downloaded: true,
            resource_thumbnail: true,
            available_to_organisations: true,
            topics: true,
            related_articles: true,
          })
            .extend({
              available_to_organisations: z.array(z.number()),
              topics: z.array(z.number()),
              related_articles: z.array(z.number()),
              file_to_be_downloaded: z
                .string()
                .optional()
                .nullable()
                .refine(v => {
                  if (!v) {
                    return true;
                  }
                  const val = ResourceWithRelated.pick({
                    file_to_be_downloaded: true,
                  }).safeParse(JSON.parse(v));

                  if (!val.success) {
                    return {
                      message: 'Invalid file.',
                    };
                  }

                  return val.success;
                }),
              resource_thumbnail: z.preprocess(
                v => {
                  if (!v) {
                    return null;
                  }

                  if (typeof v !== 'string') {
                    return JSON.stringify(v);
                  }

                  return v;
                },
                z
                  .string()
                  .optional()
                  .nullable()
                  .refine(v => {
                    if (!v) {
                      return true;
                    }
                    const val = ResourceWithRelated.pick({
                      resource_thumbnail: true,
                    }).safeParse(JSON.parse(v));

                    if (!val.success) {
                      return {
                        message: 'Invalid file.',
                      };
                    }

                    return val.success;
                  }),
              ),
            })
            .parse({
              ...values,

              // Date
              published_date: values.published_date
                ? dayjs(values.published_date).toISOString()
                : null,
              expiry_date: values.expiry_date
                ? dayjs(values.expiry_date).toISOString()
                : null,
              featured_start_date: values.featured_start_date
                ? dayjs(values.featured_start_date).toISOString()
                : null,
              featured_end_date: values.featured_end_date
                ? dayjs(values.featured_end_date).toISOString()
                : null,

              // MultiDropdown
              available_to_organisations: (
                values.available_to_organisations ?? []
              ).map((organisation: DBOrganisation) =>
                typeof organisation === 'number'
                  ? organisation
                  : organisation.id,
              ),
              topics: (values.topics ?? []).map((topic: DBTopic) =>
                typeof topic === 'number' ? topic : topic.id,
              ),
              related_articles: (values.related_articles ?? []).map(
                (article: DBResource) =>
                  typeof article === 'number' ? article : article.id,
              ),

              // FileField
              file_to_be_downloaded: values.file_to_be_downloaded,
              resource_thumbnail: values.resource_thumbnail,
            } satisfies ResourceWithRelated);

          await handleSave(formValues, id, () => {
            onSaved();
            onClose();
          });
        }}
      >
        <S9Drawer.Header
          title={id ? `Update resource #${id}` : 'Add new resource'}
          extra={
            <>
              <S9Button label="Cancel" onClick={onClose} />
              <S9EntityActions
                form={form}
                entityKey="resource"
                entityId={undefined}
                disabled={isSaving}
                isSaving={isSaving}
                onDeleted={onDeleted}
                filterPrintableDoc={() => false}
              />
            </>
          }
        />
        <S9Drawer.Content>
          {schema && (
            <>
              <S9PageTitle
                level={4}
                title="Resource details"
                className="mt-4"
              />
              <Divider className="my-3" />
              <S9Fieldset
                context={{ id }}
                schema={schema}
                formFields={[
                  {
                    field: 'resource_type',
                    value: record?.resource_type,
                    colSize: 12,
                  },
                  {
                    field: 'slug',
                    value: record?.slug,
                    colSize: 12,
                  },
                  {
                    field: 'title',
                    value: record?.title,
                  },
                ]}
              />
              {resourceType === 'Document' && (
                <S9Fieldset
                  context={{ id }}
                  schema={schema}
                  formFields={[
                    {
                      field: 'file_to_be_downloaded',
                      value: record?.file_to_be_downloaded,
                    },
                  ]}
                />
              )}
              {resourceType === 'External Link' && (
                <S9Fieldset
                  context={{ id }}
                  schema={schema}
                  formFields={[
                    {
                      field: 'external_url',
                      value: record?.external_url,
                    },
                  ]}
                />
              )}
              {resourceType === 'Vimeo' && (
                <S9Fieldset
                  context={{ id }}
                  schema={schema}
                  formFields={[
                    {
                      field: 'vimeo_video_id',
                      value: record?.vimeo_video_id,
                    },
                    {
                      field: 'video_transcript',
                      value: record?.video_transcript,
                    },
                  ]}
                />
              )}
              <S9Fieldset
                context={{ id }}
                schema={schema}
                formFields={[
                  {
                    field: 'resource_thumbnail',
                    value: record?.resource_thumbnail,
                  },
                  {
                    field: 'is_active',
                    value: record?.is_active,
                    colSize: 6,
                  },
                  {
                    field: 'is_public',
                    value: record?.is_public,
                    colSize: 6,
                  },
                  {
                    field: 'order',
                    value: record?.order,
                    colSize: 12,
                  },
                ]}
              />

              <S9PageTitle level={4} title="Publishing settings" />
              <Divider className="my-3" />

              <Row gutter={16}>
                <Col span={12}>
                  <S9DateField
                    name="published_date"
                    label="Published Date"
                    format={Utils.date.normaliseFormat(
                      config.globalization.dateFormat,
                      config.globalization.timeFormat,
                    )}
                    style={{ width: '100%' }}
                    time
                    dependencies={['expiry_date']}
                    rules={[
                      dateComparisonRule(
                        'expiry_date',
                        'before',
                        'Published date cannot be after the expiry date!',
                      ),
                    ]}
                  />
                </Col>
                <Col span={12}>
                  <S9DateField
                    name="expiry_date"
                    label="Expiry date"
                    time
                    format={Utils.date.normaliseFormat(
                      config.globalization.dateFormat,
                      config.globalization.timeFormat,
                    )}
                    dependencies={['published_date']}
                    rules={[
                      dateComparisonRule(
                        'published_date',
                        'after',
                        'Expiry date must be after the published date!',
                      ),
                    ]}
                  />
                </Col>
              </Row>

              <S9PageTitle level={4} title="Audience & visibility" />
              <Divider className="my-3" />
              <S9Fieldset
                context={{ id }}
                schema={schema}
                formFields={[
                  {
                    field: 'available_to_organisations',
                    value: record?.available_to_organisations,
                    colSize: 12,
                  },
                  {
                    field: 'topics',
                    value: record?.topics,
                    colSize: 12,
                  },
                  {
                    field: 'keywords',
                    value: record?.keywords,
                  },
                  {
                    field: 'related_articles',
                    value: record?.related_articles,
                  },
                ]}
              />

              <S9PageTitle level={4} title="Featured settings" />
              <Divider className="my-3" />
              <S9Fieldset
                context={{ id }}
                schema={schema}
                formFields={[
                  {
                    field: 'feature_on_homepage',
                    value: record?.feature_on_homepage,
                    colSize: 8,
                  },
                  {
                    field: 'featured_order',
                    value: record?.featured_order,
                    colSize: 16,
                  },
                ]}
              />

              <Row gutter={16}>
                <Col span={12}>
                  <S9DateField
                    name="featured_start_date"
                    label="Featured Start Date"
                    format={Utils.date.normaliseFormat(
                      config.globalization.dateFormat,
                      config.globalization.timeFormat,
                    )}
                    style={{ width: '100%' }}
                    time
                    dependencies={['featured_end_date']}
                    rules={[
                      dateComparisonRule(
                        'featured_end_date',
                        'before',
                        'Featured start date must be before end date!',
                      ),
                    ]}
                  />
                </Col>
                <Col span={12}>
                  <S9DateField
                    name="featured_end_date"
                    label="Featured end date"
                    time
                    format={Utils.date.normaliseFormat(
                      config.globalization.dateFormat,
                      config.globalization.timeFormat,
                    )}
                    dependencies={['featured_start_date']}
                    rules={[
                      dateComparisonRule(
                        'featured_start_date',
                        'after',
                        'Featured end date must be after start date!',
                      ),
                    ]}
                  />
                </Col>
              </Row>

              <S9PageTitle level={4} title="Resource type specific settings" />
              <Divider className="my-3" />
              <S9Fieldset
                context={{ id }}
                schema={schema}
                formFields={[
                  {
                    field: 'content',
                    value: record?.content,
                    label: 'Article content',
                  },
                ]}
              />
            </>
          )}
        </S9Drawer.Content>
      </S9Form>
    </S9Drawer>
  );
};

export default ResourceDrawer;
